import type { CombinedScenarioResult, PolicyLoanInput, PlannedRepaymentStrategyInput } from "@/lib/calculationEngine";
import { buildCommitmentPageData } from "@/lib/calculationEngine";
import type { LoanSetupData } from "@/components/LoanSetup";
import { e } from "@/lib/htmlSanitize";

function fmt(n: number): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(n);
}

function fmtPct(decimal: number): string {
  return (decimal * 100).toFixed(2) + '%';
}

const frequencyLabels: Record<string, string> = {
  monthly: 'Monthly',
  biweekly: 'Bi-Weekly',
  quarterly: 'Quarterly',
  semiannual: 'Semi-Annual',
  annual: 'Annual',
};

export type CollateralKind = 'unsecured' | 'vehicle' | 'other';
export type PrepaymentKind = 'none' | 'penalty' | 'minimum_interest';

/** Optional security, prepayment, and notarization terms for the agreement. */
export interface ThirdPartyLegalOptions {
  collateralKind: CollateralKind;
  vehicleYear: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleVin: string;
  vehicleMileage: string;
  vehicleLienholder: string;
  otherCollateral: string;
  prepaymentKind: PrepaymentKind;
  prepaymentPercent: string;
  minimumInterestMonths: string;
  notarize: boolean;
}

export const DEFAULT_THIRD_PARTY_LEGAL: ThirdPartyLegalOptions = {
  collateralKind: 'unsecured',
  vehicleYear: '',
  vehicleMake: '',
  vehicleModel: '',
  vehicleVin: '',
  vehicleMileage: '',
  vehicleLienholder: '',
  otherCollateral: '',
  prepaymentKind: 'none',
  prepaymentPercent: '2',
  minimumInterestMonths: '6',
  notarize: false,
};

interface ThirdPartyDocInput {
  loanSetup: LoanSetupData;
  combined: CombinedScenarioResult;
  policyLoanInput: Omit<PolicyLoanInput, "payments" | "projectionEndDate">;
  repaymentStrategy: PlannedRepaymentStrategyInput;
  policyOwner: string;
  loanBorrower: string;
  advisorName: string;
  governingProvince: string;
  governingCountry: string;
  relationshipToOwner: string;
  borrowerAddress: string;
  witnessName: string;
  legal?: ThirdPartyLegalOptions;
}

export function buildThirdPartyLoanHtml(input: ThirdPartyDocInput): string {
  const {
    loanSetup, combined, policyLoanInput, repaymentStrategy,
    policyOwner, loanBorrower, advisorName,
    governingProvince, governingCountry, relationshipToOwner, borrowerAddress, witnessName,
  } = input;
  const legal: ThirdPartyLegalOptions = { ...DEFAULT_THIRD_PARTY_LEGAL, ...(input.legal ?? {}) };

  const commitmentData = buildCommitmentPageData({
    clientName: loanBorrower || 'N/A',
    advisorName: advisorName || 'N/A',
    policyNumber: loanSetup.policyNumber,
    loanPurpose: loanSetup.loanTitle || 'N/A',
    policyLoanInput,
    repaymentStrategy,
    combined,
  });

  const payments = combined.plannedPayments;
  const effectiveDate = loanSetup.loanDate;
  const jurisdictionLine = governingProvince && governingCountry
    ? `the laws of ${e(governingProvince)}, ${e(governingCountry)}`
    : governingProvince
      ? `the laws of ${e(governingProvince)}`
      : 'the applicable laws of the governing jurisdiction';

  const commitmentRows = payments.map((p, i) => `
    <tr class="commit-row">
      <td class="num">${i + 1}</td>
      <td class="date">${e(p.date)}</td>
      <td class="right amt">${fmt(p.amount)}</td>
      <td class="center chk">☐</td>
      <td class="notes-line"></td>
    </tr>
  `).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Private Loan Agreement</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 10pt;
      color: #1a1a2e;
      padding: 0.5in 0.6in;
      line-height: 1.6;
    }
    h1 { font-size: 15pt; text-align: center; margin-bottom: 2pt; text-transform: uppercase; letter-spacing: 0.5pt; }
    h2 { font-size: 11pt; margin: 14pt 0 6pt; border-bottom: 1px solid #ccc; padding-bottom: 3pt; }
    .subtitle { text-align: center; font-size: 9pt; color: #666; margin-bottom: 16pt; }
    .section { margin-bottom: 14pt; }
    .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 3pt 16pt; margin-bottom: 10pt; }
    strong { font-weight: 600; }
    ol { padding-left: 18pt; }
    ol li { margin-bottom: 8pt; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 12pt; font-size: 9pt; }
    th, td { padding: 3pt 4pt; text-align: left; border-bottom: 1px solid #e5e5e5; }
    th { border-bottom: 2px solid #999; font-weight: 600; font-size: 8pt; text-transform: uppercase; letter-spacing: 0.3pt; }
    td.right, th.right { text-align: right; }
    td.center, th.center { text-align: center; }
    .sig-block { margin-bottom: 20pt; }
    .sig-block p { margin-bottom: 12pt; }
    .sig-line { display: inline-block; min-width: 340pt; border-bottom: 1px solid #333; }
    .name-line { display: inline-block; min-width: 340pt; border-bottom: 1px solid #333; font-size: 12pt; padding: 4pt 0; }
    .disclaimer { font-size: 7.5pt; color: #666; border-top: 1px solid #ccc; padding-top: 8pt; margin-top: 16pt; line-height: 1.5; }
    .page-break { page-break-before: always; }
    .commitment-box { border: 1.5px solid #1a1a2e; padding: 12pt 14pt; margin: 12pt 0; border-radius: 4pt; }
    .fill-line { display: inline-block; min-width: 180pt; border-bottom: 1px solid #999; }
    .collateral-box { border: 1px solid #ccc; padding: 10pt 12pt; margin: 10pt 0; min-height: 80pt; border-radius: 4pt; }
    .collateral-box .label { font-size: 8pt; text-transform: uppercase; letter-spacing: 0.3pt; color: #666; margin-bottom: 4pt; }
    .late-penalty-box { background: #f9f9f9; border: 1px solid #ddd; padding: 10pt 12pt; margin: 8pt 0; border-radius: 4pt; }
    table tr.commit-row td { padding-top: 6pt; padding-bottom: 6pt; }
    table td.num, table th.num { width: 22pt; }
    table td.date, table th.date { width: 62pt; }
    table td.amt, table th.amt { width: 70pt; }
    table td.chk, table th.chk { width: 28pt; font-size: 11pt; }
    table td.notes-line { border-bottom: 1px solid #333; height: 22pt; }
    @media print {
      body { padding: 0; }
      .page-break { page-break-before: always; }
    }
  </style>
</head>
<body>

  <h1>Private Loan Agreement</h1>
  <p class="subtitle">Date of Agreement: ${e(effectiveDate)}</p>

  <div class="section">
    <h2>Parties to This Agreement</h2>
    <div class="grid2">
      <div><strong>Lender:</strong> ${e(policyOwner) || '<span class="fill-line">&nbsp;</span>'}</div>
      <div><strong>Borrower:</strong> ${e(loanBorrower) || '<span class="fill-line">&nbsp;</span>'}</div>
      <div><strong>Borrower Address:</strong> ${e(borrowerAddress) || '<span class="fill-line">&nbsp;</span>'}</div>
      <div><strong>Relationship to Lender:</strong> ${e(relationshipToOwner) || '<span class="fill-line">&nbsp;</span>'}</div>
    </div>
  </div>

  <div class="section">
    <h2>Recitals</h2>
    <p>WHEREAS the Lender has agreed to advance funds to the Borrower as a private loan, subject to the terms and conditions set out in this agreement;</p>
    <p style="margin-top:6pt;">WHEREAS the Borrower has requested the use of said funds and agrees to repay the Lender in accordance with the repayment schedule attached hereto;</p>
    <p style="margin-top:6pt;">NOW THEREFORE, in consideration of the mutual covenants and promises contained herein, and for other good and valuable consideration, the receipt and sufficiency of which are hereby acknowledged by each party, the parties agree as follows:</p>
  </div>

  <div class="section">
    <h2>Terms and Conditions</h2>
    <ol>
      <li><strong>1. Loan Amount.</strong> The Lender agrees to advance to the Borrower the principal sum of <strong>${fmt(commitmentData.loanAmount)}</strong> (the "Loan Principal").</li>
      
      <li><strong>2. Purpose.</strong> The Loan shall be used for the following purpose: <strong>${e(commitmentData.loanPurpose)}</strong>. The Borrower shall not use the funds for any purpose other than that stated herein without the prior written consent of the Lender.</li>

      <li><strong>3. Interest Rate.</strong> Interest shall accrue on the outstanding principal balance at a rate of <strong>${fmtPct(commitmentData.chosenRepaymentRate)}</strong> per annum, compounded in accordance with the payment frequency described below.</li>

      <li><strong>4. Repayment Schedule.</strong> The Borrower shall make <strong>${commitmentData.targetNumberOfPayments}</strong> ${e(frequencyLabels[commitmentData.paymentFrequency]?.toLowerCase() ?? '')} payments of <strong>${fmt(commitmentData.targetPaymentAmount)}</strong> each, commencing on <strong>${e(payments[0]?.date) || 'TBD'}</strong> and continuing until the Loan Principal and all accrued interest are repaid in full. The complete repayment schedule is attached hereto as Schedule "A" and forms part of this agreement.</li>

      <li><strong>5. Late Payment Penalties.</strong>
        <div class="late-penalty-box">
          <p>(a) If any scheduled payment is not received within <strong>seven (7) calendar days</strong> of the due date, the Borrower shall pay a late fee equal to <strong>five percent (5%)</strong> of the overdue payment amount, or <strong>$25.00</strong>, whichever is greater.</p>
          <p style="margin-top:4pt;">(b) If any payment remains unpaid for more than <strong>thirty (30) calendar days</strong> past the due date, the outstanding balance shall bear interest at a default rate of <strong>${fmtPct(commitmentData.chosenRepaymentRate + 0.05)}</strong> per annum (the contractual rate plus 5.00%) until the default is cured.</p>
          <p style="margin-top:4pt;">(c) Acceptance of a late payment by the Lender shall not constitute a waiver of the Lender's right to enforce any provision of this agreement or to declare the Loan in default.</p>
        </div>
      </li>

      <li><strong>6. Default.</strong> The Loan shall be in default upon the occurrence of any of the following events:
        <p style="margin-top:4pt;">(a) The Borrower fails to make any payment within thirty (30) calendar days of its due date;</p>
        <p>(b) The Borrower becomes insolvent, files for bankruptcy protection, or has a petition in bankruptcy filed against them;</p>
        <p>(c) Any material representation made by the Borrower in connection with this agreement is found to be false, misleading, or fraudulent;</p>
        <p>(d) The Borrower breaches any other material term of this agreement and fails to cure such breach within fifteen (15) days of written notice from the Lender.</p>
        <p style="margin-top:4pt;">Upon default, the Lender may, at their sole discretion, declare the entire outstanding balance of principal and accrued interest immediately due and payable, and pursue any and all remedies available at law or in equity, including but not limited to the recovery of reasonable legal fees and costs of collection.</p>
      </li>

      <li><strong>7. Prepayment.</strong> ${
        legal.prepaymentKind === 'penalty'
          ? `The Borrower may prepay the Loan in whole or in part at any time, provided that any prepayment of principal shall be accompanied by a prepayment premium equal to <strong>${e(legal.prepaymentPercent || '0')}%</strong> of the principal amount prepaid. The prepayment premium is due at the time of the prepayment and shall not reduce the outstanding principal balance.`
          : legal.prepaymentKind === 'minimum_interest'
            ? `The Borrower may prepay the Loan in whole or in part at any time, provided that the Lender shall be entitled to receive a minimum of <strong>${e(legal.minimumInterestMonths || '0')} month(s)</strong> of interest at the contractual rate. If the Loan is repaid in full before that minimum interest period has elapsed, the Borrower shall pay the difference between the interest actually paid and the minimum interest due.`
            : `The Borrower may prepay the Loan in whole or in part at any time without penalty or premium.`
      }</li>

      <li><strong>8. Collateral / Security.</strong>
        ${legal.collateralKind === 'vehicle' ? `
        <p style="margin-top:4pt;">This Loan is <strong>secured</strong>. The Borrower pledges and grants to the Lender a security interest in the following motor vehicle or titled equipment (the "Collateral"):</p>
        <div class="collateral-box">
          <div class="label">Vehicle / Equipment Collateral</div>
          <p>Year: <strong>${e(legal.vehicleYear) || '_____________'}</strong> &nbsp; Make: <strong>${e(legal.vehicleMake) || '_____________'}</strong> &nbsp; Model: <strong>${e(legal.vehicleModel) || '_____________'}</strong></p>
          <p>VIN / Serial Number: <strong>${e(legal.vehicleVin) || '____________________________'}</strong></p>
          <p>Odometer / Hours at execution: <strong>${e(legal.vehicleMileage) || '_____________'}</strong></p>
          <p>Existing lienholder (if any): <strong>${e(legal.vehicleLienholder) || 'None disclosed'}</strong></p>
        </div>
        <p style="font-size:9pt;">The Borrower shall keep the Collateral insured for its full replacement value, shall not sell, transfer, or further encumber the Collateral while any amount remains outstanding, and shall deliver the certificate of title or registration to the Lender or record the Lender's security interest as required in the governing jurisdiction. Upon a default event, the Lender may take possession of and dispose of the Collateral in accordance with applicable personal property security or secured transactions law, and apply the proceeds to the outstanding balance.</p>
        ` : legal.collateralKind === 'other' ? `
        <p style="margin-top:4pt;">This Loan is <strong>secured</strong>. The Borrower pledges and grants to the Lender a security interest in the following property or assets:</p>
        <div class="collateral-box">
          <div class="label">Description of Collateral</div>
          <p>${e(legal.otherCollateral) || '&nbsp;'}</p>
          <p>&nbsp;</p>
        </div>
        <p style="font-size:9pt;">The Borrower represents and warrants that they have full authority to pledge such collateral, that it is free and clear of all liens and encumbrances unless otherwise disclosed above, and that the Lender shall have the right to take possession of or liquidate such collateral upon a default event to satisfy the outstanding balance.</p>
        ` : `
        <p style="margin-top:4pt;">This Loan is <strong>unsecured</strong>. No collateral has been pledged. If the parties later agree to secure this Loan, they shall do so by written amendment describing the collateral in reasonable detail.</p>
        `}
      </li>

      <li><strong>9. No Assignment.</strong> Neither party may assign or transfer any rights or obligations under this agreement without the prior written consent of the other party.</li>

      <li><strong>10. Notices.</strong> All notices required or permitted under this agreement shall be in writing and shall be deemed delivered when: (a) delivered personally; (b) sent by registered or certified mail, return receipt requested; or (c) sent by email with confirmed receipt. Notices shall be sent to the addresses set forth above or to such other address as either party may designate in writing.</li>

      <li><strong>11. Entire Agreement.</strong> This agreement, together with the attached repayment schedule, constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior discussions, negotiations, representations, warranties, and agreements, whether written or oral.</li>

      <li><strong>12. Amendments.</strong> This agreement may not be amended, modified, or supplemented except by a written instrument signed by both the Lender and the Borrower.</li>

      <li><strong>13. Waiver.</strong> The failure of either party to enforce any provision of this agreement shall not be construed as a waiver of such provision or the right to enforce it at a later time.</li>

      <li><strong>14. Governing Law.</strong> This agreement shall be governed by and construed in accordance with ${jurisdictionLine}. Any dispute arising out of or in connection with this agreement shall be submitted to the exclusive jurisdiction of the courts of the governing jurisdiction.</li>

      <li><strong>15. Severability.</strong> If any provision of this agreement is held to be invalid, illegal, or unenforceable by a court of competent jurisdiction, the validity, legality, and enforceability of the remaining provisions shall not in any way be affected or impaired.</li>
    </ol>
  </div>

  <div class="commitment-box">
    <p><strong>Borrower's Acknowledgment and Commitment</strong></p>
    <p style="margin-top:6pt;">I, <strong>${e(loanBorrower) || '_______________'}</strong>, hereby acknowledge that I have read, understood, and agree to be bound by all terms and conditions set forth in this Private Loan Agreement. I commit to repaying the Loan Principal together with all accrued interest in full and on time as outlined in the attached repayment schedule (Schedule "A"). I understand that failure to meet my payment obligations may result in late fees, default interest, acceleration of the full outstanding balance, enforcement against any pledged collateral, and legal action for recovery of all amounts owed including reasonable legal costs.</p>
  </div>

  <div class="page-break"></div>

  <h2>Schedule "A" — Repayment Schedule (${payments.length} Payments)</h2>
  <div class="grid2" style="margin-bottom:8pt;">
    <div>Loan Amount: <strong>${fmt(commitmentData.loanAmount)}</strong></div>
    <div>Interest Rate: <strong>${fmtPct(commitmentData.chosenRepaymentRate)}</strong> per annum</div>
    <div>Payment Frequency: <strong>${e(frequencyLabels[commitmentData.paymentFrequency])}</strong></div>
    <div>Payment Amount: <strong>${fmt(commitmentData.targetPaymentAmount)}</strong></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>#</th>
        <th>Due Date</th>
        <th class="right">Amount</th>
        <th class="center">Paid ☐</th>
        <th>Date Paid</th>
        <th>Notes</th>
      </tr>
    </thead>
    <tbody>
      ${commitmentRows}
    </tbody>
  </table>

  <div class="page-break"></div>

  <h2>Execution</h2>
  <p style="margin-bottom:16pt;">IN WITNESS WHEREOF, the parties hereto have executed this Private Loan Agreement as of the date first written above.</p>

  <div class="sig-block">
    <p><strong>Lender</strong></p>
    <p>Printed Name: ${e(policyOwner) || '<span class="name-line">&nbsp;</span>'}</p>
    <p>Signature: <span class="sig-line">&nbsp;</span></p>
    <p>Date: <span class="sig-line">&nbsp;</span></p>
  </div>

  <div class="sig-block">
    <p><strong>Borrower</strong></p>
    <p>Printed Name: ${e(loanBorrower) || '<span class="name-line">&nbsp;</span>'}</p>
    <p>Address: ${e(borrowerAddress) || '<span class="name-line">&nbsp;</span>'}</p>
    <p>Signature: <span class="sig-line">&nbsp;</span></p>
    <p>Date: <span class="sig-line">&nbsp;</span></p>
  </div>

  ${witnessName ? `
  <div class="sig-block">
    <p><strong>Witness</strong></p>
    <p>Printed Name: ${e(witnessName)}</p>
    <p>Signature: <span class="sig-line">&nbsp;</span></p>
    <p>Date: <span class="sig-line">&nbsp;</span></p>
  </div>
  ` : `
  <div class="sig-block">
    <p><strong>Witness (Optional)</strong></p>
    <p>Printed Name: <span class="sig-line">&nbsp;</span></p>
    <p>Signature: <span class="sig-line">&nbsp;</span></p>
    <p>Date: <span class="sig-line">&nbsp;</span></p>
  </div>
  `}

  ${legal.notarize ? `
  <div class="section" style="margin-top:18pt;">
    <h2>Notary Public Acknowledgement</h2>
    <p>State / Province of: <span class="sig-line">&nbsp;</span></p>
    <p>County / District of: <span class="sig-line">&nbsp;</span></p>
    <p style="margin-top:8pt;">On this _______ day of _________________, 20______, before me personally appeared
      <strong>${e(policyOwner) || '_______________'}</strong> (Lender) and
      <strong>${e(loanBorrower) || '_______________'}</strong> (Borrower), known to me or satisfactorily proven to be
      the persons whose names are subscribed to the foregoing Private Loan Agreement, and acknowledged that they
      executed the same for the purposes therein contained.</p>
    <p style="margin-top:12pt;">Notary Signature: <span class="sig-line">&nbsp;</span></p>
    <p>Printed Name: <span class="sig-line">&nbsp;</span></p>
    <p>My commission expires: <span class="sig-line">&nbsp;</span></p>
    <div class="collateral-box" style="margin-top:10pt; height:70pt;">
      <div class="label">Notary Seal / Stamp</div>
    </div>
  </div>
  ` : ''}

  <div class="late-penalty-box" style="margin-top:16pt;">
    <p><strong>IMPORTANT — EDUCATIONAL TEMPLATE, NOT LEGAL ADVICE.</strong> This document is a general educational
    template produced by planning software. It has not been prepared or reviewed by a lawyer for your situation.
    Secured loans, vehicle or equipment collateral, prepayment premiums, minimum-interest provisions, default
    interest, and notarization requirements are governed by rules that differ by state, province, and country, and
    some provisions may be unenforceable or unlawful where you live. Have a qualified lawyer in the governing
    jurisdiction review and adapt this agreement before either party signs it.</p>
  </div>

  <p class="disclaimer"><strong>Disclaimer:</strong> This Private Loan Agreement is intended to document a private lending arrangement between the named parties. It is drafted based on general common law principles applicable across North American jurisdictions. This document does not constitute legal, financial, or tax advice. Both parties are strongly encouraged to seek independent legal counsel before executing this agreement. The enforceability of specific provisions may vary by jurisdiction, and the parties should ensure compliance with all applicable local, state/provincial, and federal laws, including any applicable usury or consumer lending statutes.</p>

</body>
</html>`;
}
